import {
  AlertCircle,
  LoaderIcon,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { DateRange } from "@/lib/date";

export function ProductUsage({
  productId,
  dateRange,
}: {
  productId: string;
  dateRange: DateRange;
}) {
  const { data: singleProductAnalytics, isLoading: isSingleLoading } = useQuery(
    productAnalyticsQueries.product(
      productId,
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
  );

  return (
    <>
      {isSingleLoading ? (
        <div className="h-[200px] flex items-center justify-center">
          <LoaderIcon className="h-8 w-8 animate-spin" />
        </div>
      ) : singleProductAnalytics ? (
        <Card className="p-4 mb-6">
          <h3 className="text-lg font-semibold mb-4">Usage by Time Period</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Avg Credits/Call</TableHead>
                <TableHead className="text-right">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {singleProductAnalytics.usage_periods?.map((p) => {
                const c = p.credits ?? 0;
                const calls = p.calls ?? 0;
                return (
                  <TableRow key={p.name}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {c.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {calls.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {calls > 0 ? (c / calls).toFixed(2) : "0.00"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.growth >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        <span
                          className={
                            p.growth >= 0 ? "text-green-500" : "text-red-500"
                          }
                        >
                          {Math.abs(p.growth).toFixed(1)}%
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mr-2" />
          No usage analysis
        </div>
      )}
    </>
  );
}
